import { useCallback, useEffect, useRef, useState } from "react";
import globalState, { GlobalState } from "../../globalState";
import { useIntl } from "react-intl";
import hookMessages from "../messages";
import { ChatMessage } from "myfitworld-model";
import { addNewMessage, createChatAndAddNewMessage, fetchOlderMessages, readMessages, subscribeToMessagesForChat } from "src/api/messagesApi";
import { useUserProvider } from "src/providers/UserProvider";
const START_LIMIT = 30;
const LOAD_MORE_LIMIT = 10;

function useFetchMessagesForChat({ chatIds }: { chatIds: string[]}) {
  const { formatMessage } = useIntl();
  const {user} = useUserProvider();
  const [allMessages, setAllMessages] = useState<ChatMessage[]>([]);
  const [lastId, setLastId] = useState<string | undefined>(undefined);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const unsubscribeRef = useRef<() => void>();

  useEffect(() => {
    if (!chatIds) return;
    const unsubscribe = subscribeToMessagesForChat(chatIds, START_LIMIT, (newMessages: ChatMessage[]) => {
      setIsLoading(false);
      setAllMessages(prevMessages => {
        const newIds = new Set(prevMessages.map(m => m.id));
        const merged = [...prevMessages, ...newMessages.filter(m => !newIds.has(m.id))];
        if (merged.length === 0 || merged.length < START_LIMIT) {
          setHasMore(false);
        }
        if(merged.length > 0){
          setLastId(merged[merged.length - 1].id);
          setChatId(merged[0].chatId);
        }
        return merged.sort((a, b) =>
          a.createdAt.getTime() - b.createdAt.getTime()
        );
      });
    }, () => onCatchError());
    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatIds]);



  const onCatchError = () => {
    globalState.update((state: GlobalState) => {
      state.toastQueue.push({
        message: formatMessage(hookMessages.error_loading_messages),
        severity: "error",
      });
    });
  }

  const fetchMoreMessages = useCallback(async () => {
    if (!chatId || !lastId) return;
    setIsLoadingMore(true);
    try {
      const newDocs = await fetchOlderMessages( chatId, lastId, LOAD_MORE_LIMIT);
      if (newDocs.length === 0 || newDocs.length < LOAD_MORE_LIMIT) {
        setHasMore(false);
      }
      if(newDocs.length > 0){
        setLastId(newDocs[newDocs.length - 1].id);
        setChatId(newDocs[0].chatId);
      }

      setAllMessages((prevMessages) => {
        const seenIds = new Set(prevMessages.map((m) => m.id));
        const uniqueOlder = newDocs.filter((m) => !seenIds.has(m.id));
        return [...prevMessages, ...uniqueOlder].sort((a, b) => (a.createdAt).getTime() - ((b.createdAt).getTime()))
      });
      setIsLoadingMore(false);

    } catch (error) {
      globalState.update((state: GlobalState) => {
        state.toastQueue.push({
          message: formatMessage(hookMessages.error_loading_messages),
          severity: "error",
        });
      });
    }
  }, [chatId, lastId, formatMessage]);

  const markAsReadMessages = async () => {
    const messagesForReadingByChatId: Map<string, string[]> = new Map();
    allMessages
      .filter((message) => !message.read && message.id && message.chatId && message.senderId !== user?.id)
      .forEach((message) => {
        const chatId = message.chatId as string;
        const messageId = message.id as string;
    
        if (!messagesForReadingByChatId.has(chatId)) {
          messagesForReadingByChatId.set(chatId, []);
        }
    
        messagesForReadingByChatId.get(chatId)?.push(messageId);
      });
;    const succes = await readMessages(messagesForReadingByChatId);
    if(succes) {
      setAllMessages((prevMessages) =>
          prevMessages.map((message) =>
              !message.read ? { ...message, read: true } : message
        )
      );
    }
  }

  const onSendMessage = async (message: ChatMessage) => {
    try {
      if(message.chatId !== "") {
        await addNewMessage(message, message.chatId);
      } else {
        await createChatAndAddNewMessage(message);
      }
    } catch (error) {
      console.log(error);
      globalState.update((state: GlobalState) => {
        state.toastQueue.push({
          message: formatMessage(hookMessages.error_adding_messages),
          severity: "error",
        });
      });
    }
  }


  return {
    messages: allMessages,
    isLoading,
    isLoadingMore,
    lastId,
    chatId,
    hasMore,
    fetchMoreMessages,
    onSendMessage,
    markAsReadMessages
  };
}

export default useFetchMessagesForChat;
